/**
 * Ella Radio track registry — server mirror vs enqueue dedup.
 * _serverKnownIds: full server list (defer counts/diagnostics; never blocks append).
 * _enqueuedIds: tracks that entered the transport queue this session (dedup authority).
 */

import { useMaestroStore } from '../store/maestroStore';
import { ellaRadioSessionRefs } from './ellaRadioSessionRefs';

const _serverKnownIds = new Set<string>();
const _enqueuedIds = new Set<string>();

export function resetRadioTrackRegistry(): void {
  _serverKnownIds.clear();
  _enqueuedIds.clear();
}

/** Drop enqueued markers for tracks no longer in the transport queue (stale after replace). */
export function clearStaleRadioEnqueuedIds(): number {
  const queueIds = new Set(getQueueTrackIds());
  let cleared = 0;
  for (const id of _enqueuedIds) {
    if (!queueIds.has(id)) {
      _enqueuedIds.delete(id);
      cleared += 1;
    }
  }
  return cleared;
}

/** Seed opener / launch tracks — already enqueued via playFrom. */
export function seedRadioTrackRegistry(ids: string[]): void {
  for (const id of ids) {
    if (!id) continue;
    const s = String(id);
    _serverKnownIds.add(s);
    _enqueuedIds.add(s);
  }
}

/** Mirror the server's full track list (no queue mutation, no enqueue block). */
export function reconcileRadioTrackRegistryFromServer(trackIds: string[]): void {
  for (const raw of trackIds) {
    const id = String(raw || '').trim();
    if (id) _serverKnownIds.add(id);
  }
}

/** Mark queue occupants as enqueued after playFrom / launch / reconcile. */
export function syncRadioTrackRegistryFromQueue(): void {
  for (const id of getQueueTrackIds()) {
    _serverKnownIds.add(id);
    _enqueuedIds.add(id);
  }
}

/** Server-known track ids (arc mirror) — for defer counts and diagnostics. */
export function getRadioTrackRegistryIds(): string[] {
  return [..._serverKnownIds];
}

export function getRadioEnqueuedTrackIds(): string[] {
  return [..._enqueuedIds];
}

/** Registry tracks not yet in the transport queue (server-known, pending enqueue). */
export function countRadioRegistryUnplayedBeyondQueue(): number {
  if (!isRadioQueueSession()) return 0;
  const queueIds = new Set(getQueueTrackIds());
  return getRadioTrackRegistryIds().filter((id) => !queueIds.has(id)).length;
}

/** True when a server-known track should still be appended to the transport queue. */
export function isRadioTrackPendingEnqueue(trackId: string): boolean {
  if (!trackId) return false;
  if (isTrackIdInQueue(trackId)) return false;
  if (isTrackIdPlayedThisSession(trackId)) return false;
  if (_enqueuedIds.has(trackId)) return false;
  return true;
}

export function extractTrackId(track: unknown): string | null {
  if (!track || typeof track !== 'object') return null;
  const t = (track as { track?: unknown }).track ?? track;
  const row = t as Record<string, unknown>;
  const id = row.qobuzTrackId || row.id || row.trackId;
  return id ? String(id) : null;
}

export function getQueueTrackIds(): string[] {
  const store = useMaestroStore.getState();
  return store.queue
    .map((item) => extractTrackId(item))
    .filter((id): id is string => Boolean(id));
}

export function isRadioQueueSession(): boolean {
  if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('active_ella_radio') === 'true') {
    return true;
  }
  return useMaestroStore.getState().activeSource === 'radio';
}

function isTrackIdInQueue(trackId: string): boolean {
  return getQueueTrackIds().includes(trackId);
}

function isTrackIdPlayedThisSession(trackId: string): boolean {
  try {
    const played = ellaRadioSessionRefs.getPlayedTrackIds?.() || [];
    return played.map(String).includes(trackId);
  } catch {
    return false;
  }
}

export function isRadioTrackDuplicate(trackId: string): boolean {
  if (isTrackIdInQueue(trackId)) return true;
  if (_enqueuedIds.has(trackId)) return true;
  if (isTrackIdPlayedThisSession(trackId)) return true;
  return false;
}

/** Filter radio enqueue candidates; logs [RadioDedup] on every rejection. */
export function filterForRadioEnqueue(tracks: unknown[], sourcePath: string): unknown[] {
  if (!tracks.length) return [];
  const accepted: unknown[] = [];

  for (const track of tracks) {
    const id = extractTrackId(track);
    if (!id) {
      accepted.push(track);
      continue;
    }
    if (isTrackIdInQueue(id)) {
      console.info(`[RadioDedup] client-side reject track_id=${id} source=${sourcePath} reason=queue`);
      continue;
    }
    if (isTrackIdPlayedThisSession(id)) {
      console.info(`[RadioDedup] client-side reject track_id=${id} source=${sourcePath} reason=played`);
      continue;
    }
    if (_enqueuedIds.has(id)) {
      console.info(`[RadioDedup] client-side reject track_id=${id} source=${sourcePath} reason=session`);
      continue;
    }
    _enqueuedIds.add(id);
    accepted.push(track);
  }

  return accepted;
}
